import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  BellRing,
  Check,
  Heart,
  MapPinned,
  MessageCircleMore,
  UserRoundPlus,
  UsersRound,
} from "lucide-react";
import SettingsPanel from "../../components/SettingsPanel";
import usePushRegistrationController from "../../components/settings/notification-preferences/usePushRegistrationController";
import { requestNotificationPreferencesRefresh } from "../../components/settings/notification-preferences/notificationPreferencesEvents";
import { getPushCapability } from "../../services/pushCapability";
import {
  isPushPermissionUiEnabled,
  requestNotificationPermissionFromUserGesture,
} from "../../services/pushPermission";
import {
  PUSH_ONBOARDING_CONSENT_VERSION,
  PUSH_ONBOARDING_OPT_IN_SOURCE,
} from "../../services/pushRegistrationsApi";
import { getOrCreatePushInstallation } from "../../services/pushInstallation";
import {
  dismissPushOnboarding,
  fetchPushOnboardingState,
  markPushOnboardingShown,
} from "./notificationOnboardingApi";
import "../../styles/components/push-notification-onboarding.css";

export const PUSH_ONBOARDING_ENV = "REACT_APP_PUSH_ONBOARDING_ENABLED";
export const PUSH_ONBOARDING_REQUEST_TIMEOUT_MS = 8000;
export const PUSH_ONBOARDING_MAX_ATTEMPTS = 3;
const PUSH_ONBOARDING_RETRY_DELAYS_MS = [250, 1000];
const SESSION_KEY = "reelitpro:push-onboarding:v1:shown";

const BENEFITS = [
  [MessageCircleMore, "push_onboarding_benefit_messages"],
  [BellRing, "push_onboarding_benefit_replies"],
  [UserRoundPlus, "push_onboarding_benefit_friends"],
  [Heart, "push_onboarding_benefit_social"],
  [MapPinned, "push_onboarding_benefit_trips"],
];

export function isPushOnboardingEnabled(environment = process.env) {
  return environment?.[PUSH_ONBOARDING_ENV] === "true";
}

function installationSessionKey(accountKey, installationId) {
  return `${SESSION_KEY}:${String(accountKey)}:${String(installationId)}`;
}

function sessionAlreadyShown(accountKey, installationId) {
  try {
    return window.sessionStorage.getItem(
      installationSessionKey(accountKey, installationId)
    ) === "true";
  } catch {
    return false;
  }
}

function rememberSession(accountKey, installationId) {
  try {
    window.sessionStorage.setItem(
      installationSessionKey(accountKey, installationId),
      "true"
    );
  } catch {
    // Durable backend timestamps still prevent refresh loops.
  }
}

function focusableElements(container) {
  return Array.from(
    container?.querySelectorAll?.(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ) || []
  );
}

function abortError() {
  const error = new Error("push_onboarding_aborted");
  error.name = "AbortError";
  return error;
}

function timeoutError() {
  const error = new Error("push_onboarding_timeout");
  error.name = "PushOnboardingTimeoutError";
  error.classification = "timeout";
  return error;
}

function isRetryableOnboardingError(error) {
  if (error?.classification === "timeout") return true;
  if (error?.classification === "network") return true;
  if (error?.classification !== "http") return false;
  return (
    error.status === 408 ||
    error.status === 425 ||
    error.status === 429 ||
    Number(error.status) >= 500
  );
}

function waitForRetry(delayMs, signal) {
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise((resolve, reject) => {
    const timerId = window.setTimeout(() => {
      signal.removeEventListener("abort", handleAbort);
      resolve();
    }, delayMs);
    const handleAbort = () => {
      window.clearTimeout(timerId);
      signal.removeEventListener("abort", handleAbort);
      reject(abortError());
    };
    signal.addEventListener("abort", handleAbort, { once: true });
  });
}

async function requestWithTimeout(request, scopeSignal) {
  if (scopeSignal.aborted) throw abortError();
  const controller = new AbortController();
  let timerId;
  let handleScopeAbort;
  const timeout = new Promise((_, reject) => {
    timerId = window.setTimeout(() => {
      controller.abort();
      reject(timeoutError());
    }, PUSH_ONBOARDING_REQUEST_TIMEOUT_MS);
  });
  const scopeAbort = new Promise((_, reject) => {
    handleScopeAbort = () => {
      controller.abort();
      reject(abortError());
    };
    scopeSignal.addEventListener("abort", handleScopeAbort, { once: true });
  });
  try {
    return await Promise.race([
      Promise.resolve().then(() => request(controller.signal)),
      timeout,
      scopeAbort,
    ]);
  } finally {
    window.clearTimeout(timerId);
    scopeSignal.removeEventListener("abort", handleScopeAbort);
  }
}

export default function PushNotificationOnboarding({
  accountKey,
  currentUser,
  appMode,
  onAppModeChange,
  themeMode,
  onThemeModeChange,
}) {
  const { t } = useTranslation();
  const registration = usePushRegistrationController();
  const [visible, setVisible] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [status, setStatus] = useState("idle");
  const [busy, setBusy] = useState(false);
  const dialogRef = useRef(null);
  const primaryRef = useRef(null);
  const actionControllerRef = useRef(null);
  const installationIdRef = useRef(null);
  const eligibilityGenerationRef = useRef(0);

  useEffect(() => {
    const generation = ++eligibilityGenerationRef.current;
    actionControllerRef.current?.abort();
    actionControllerRef.current = null;
    setVisible(false);
    setSettingsOpen(false);
    setStatus("idle");
    setBusy(false);
    const installationId = getOrCreatePushInstallation().installationId;
    installationIdRef.current = installationId;
    let capability;
    try {
      capability = getPushCapability();
    } catch {
      capability = { supported: false, configured: false, permissionState: "unsupported" };
    }
    const permission = capability.permissionState;
    if (
      !accountKey ||
      !installationId ||
      !isPushOnboardingEnabled() ||
      !isPushPermissionUiEnabled() ||
      !capability.supported ||
      !capability.configured ||
      !registration.registrationEnabled ||
      !registration.vapidConfigured ||
      registration.active ||
      !["default", "granted"].includes(permission) ||
      sessionAlreadyShown(accountKey, installationId)
    ) {
      return undefined;
    }

    const scopeController = new AbortController();
    let cyclePromise = null;
    let terminal = false;
    const isCurrent = () =>
      !scopeController.signal.aborted &&
      eligibilityGenerationRef.current === generation &&
      installationIdRef.current === installationId;

    const runCycle = async () => {
      for (let attempt = 0; attempt < PUSH_ONBOARDING_MAX_ATTEMPTS; attempt += 1) {
        try {
          const state = await requestWithTimeout(
            (signal) => fetchPushOnboardingState({ installationId, signal }),
            scopeController.signal
          );
          if (!isCurrent()) return;
          const currentCapability = getPushCapability();
          if (
            !state.eligible ||
            state.consentRecorded ||
            !currentCapability.supported ||
            !currentCapability.configured ||
            currentCapability.permissionState === "denied"
          ) {
            terminal = true;
            return;
          }
          const shown = await requestWithTimeout(
            (signal) => markPushOnboardingShown({ installationId, signal }),
            scopeController.signal
          );
          if (!isCurrent()) return;
          terminal = true;
          if (!shown.shown) return;
          rememberSession(accountKey, installationId);
          setVisible(true);
          return;
        } catch (error) {
          if (error?.name === "AbortError" || !isCurrent()) return;
          if (!isRetryableOnboardingError(error)) {
            terminal = true;
            return;
          }
          if (attempt + 1 >= PUSH_ONBOARDING_MAX_ATTEMPTS) return;
          try {
            await waitForRetry(
              PUSH_ONBOARDING_RETRY_DELAYS_MS[attempt] || 0,
              scopeController.signal
            );
          } catch {
            return;
          }
        }
      }
    };

    const startCycle = () => {
      if (terminal || cyclePromise || !isCurrent()) return;
      cyclePromise = runCycle()
        .catch(() => undefined)
        .finally(() => {
          cyclePromise = null;
        });
    };

    const handleRecovery = () => startCycle();
    const handleVisibilityRecovery = () => {
      if (document.visibilityState === "visible") startCycle();
    };
    window.addEventListener("online", handleRecovery);
    window.addEventListener("focus", handleRecovery);
    document.addEventListener("visibilitychange", handleVisibilityRecovery);
    // Deferring one task prevents React Strict Mode's probe effect from issuing a request.
    const timerId = window.setTimeout(startCycle, 0);
    return () => {
      window.clearTimeout(timerId);
      window.removeEventListener("online", handleRecovery);
      window.removeEventListener("focus", handleRecovery);
      document.removeEventListener("visibilitychange", handleVisibilityRecovery);
      scopeController.abort();
    };
  }, [
    accountKey,
    registration.active,
    registration.registrationEnabled,
    registration.vapidConfigured,
  ]);

  useEffect(() => () => actionControllerRef.current?.abort(), []);

  const handleNotNow = async () => {
    if (busy) return;
    setBusy(true);
    setStatus("dismissing");
    const controller = new AbortController();
    actionControllerRef.current?.abort();
    actionControllerRef.current = controller;
    try {
      const dismissed = await dismissPushOnboarding({
        installationId: installationIdRef.current,
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      if (!dismissed.dismissed) {
        setStatus("dismiss_failed");
        return;
      }
      rememberSession(accountKey, installationIdRef.current);
      setVisible(false);
      setStatus("idle");
    } catch (error) {
      if (error?.name !== "AbortError" && !controller.signal.aborted) {
        setStatus("dismiss_failed");
      }
    } finally {
      if (actionControllerRef.current === controller) actionControllerRef.current = null;
      if (!controller.signal.aborted) setBusy(false);
    }
  };

  useEffect(() => {
    if (!visible) return undefined;
    const previousFocus = document.activeElement;
    primaryRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !busy) {
        event.preventDefault();
        handleNotNow();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = focusableElements(dialogRef.current);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previousFocus?.focus?.();
    };
  });

  const handleEnable = async () => {
    if (busy) return;
    setBusy(true);
    setStatus("requesting");
    const permission = await requestNotificationPermissionFromUserGesture();
    if (permission.permission !== "granted") {
      setStatus(
        permission.permission === "denied"
          ? "permission_denied"
          : "permission_not_granted"
      );
      setBusy(false);
      return;
    }
    setStatus("registering");
    const registered = await registration.actions.registerCurrentDevice({
      permissionGranted: true,
      consent: {
        version: PUSH_ONBOARDING_CONSENT_VERSION,
        source: PUSH_ONBOARDING_OPT_IN_SOURCE,
      },
    });
    if (registered) requestNotificationPreferencesRefresh();
    setBusy(false);
    setStatus(registered ? "success" : "registration_failed");
  };

  const handleCustomize = () => {
    if (busy) return;
    rememberSession(accountKey, installationIdRef.current);
    setVisible(false);
    setSettingsOpen(true);
  };

  const modal = visible ? (
    <div className="rp-push-onboarding__backdrop" role="presentation">
      <section
        ref={dialogRef}
        className="rp-push-onboarding"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rp-push-onboarding-title"
        aria-describedby="rp-push-onboarding-description"
      >
        {status === "success" ? (
          <div className="rp-push-onboarding__success" role="status" aria-live="polite">
            <span className="rp-push-onboarding__success-icon" aria-hidden="true">
              <Check size={34} />
            </span>
            <h2 id="rp-push-onboarding-title">{t("push_onboarding_success_title")}</h2>
            <p id="rp-push-onboarding-description">{t("push_onboarding_success_description")}</p>
            <button
              ref={primaryRef}
              type="button"
              className="rp-push-onboarding__primary"
              onClick={() => setVisible(false)}
            >
              {t("push_onboarding_done")}
            </button>
          </div>
        ) : (
          <>
            <div className="rp-push-onboarding__eyebrow">
              <BellRing size={18} aria-hidden="true" />
              {t("push_onboarding_eyebrow")}
            </div>
            <h2 id="rp-push-onboarding-title">{t("push_onboarding_title")}</h2>
            <p id="rp-push-onboarding-description" className="rp-push-onboarding__description">
              {t("push_onboarding_description")}
            </p>
            <div className="rp-push-onboarding__benefits">
              {BENEFITS.map(([Icon, key]) => (
                <div className="rp-push-onboarding__benefit" key={key}>
                  <Icon size={20} aria-hidden="true" />
                  <span>{t(key)}</span>
                </div>
              ))}
            </div>
            <p className="rp-push-onboarding__control">{t("push_onboarding_control")}</p>
            <p className="rp-push-onboarding__marketing">{t("push_onboarding_marketing_optional")}</p>
            {status !== "idle" ? (
              <p className={`rp-push-onboarding__status rp-push-onboarding__status--${status}`} role="status" aria-live="polite">
                {t(`push_onboarding_status_${status}`)}
              </p>
            ) : null}
            <div className="rp-push-onboarding__actions">
              <button ref={primaryRef} type="button" className="rp-push-onboarding__primary" disabled={busy} onClick={handleEnable}>
                {t("push_onboarding_enable")}
              </button>
              <button type="button" className="rp-push-onboarding__secondary" disabled={busy} onClick={handleCustomize}>
                <UsersRound size={17} aria-hidden="true" />
                {t("push_onboarding_customize")}
              </button>
              <button type="button" className="rp-push-onboarding__tertiary" disabled={busy} onClick={handleNotNow}>
                {t("push_onboarding_not_now")}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  ) : null;

  return (
    <>
      {modal ? createPortal(modal, document.body) : null}
      <SettingsPanel
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        initialPage="push"
        appMode={appMode}
        onAppModeChange={onAppModeChange}
        themeMode={themeMode}
        onThemeModeChange={onThemeModeChange}
        currentUser={currentUser}
      />
    </>
  );
}
